<script setup lang="ts">
import WrSVGIcon from "~/components/global/WrSVGIcon.vue";
import WrIcon from "~/components/global/WrIcon.vue";

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

interface DualCard {
  title: string;
  description: string;
  icon?: string;
  svgIcon?: string;
}

interface Props {
  heading?: string;
  subtitle?: string;
  groupOneTitle?: string;
  groupOneCards?: DualCard[];
  groupTwoTitle?: string;
  groupTwoCards?: DualCard[];
  ctaText?: string;
  ctaLink?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  heading: "How AI Agents and Humans Collaborate",
  subtitle: "AI agents execute end-to-end workflows. Human experts make the strategic calls.",
  groupOneTitle: "AI Agents do this:",
  groupOneCards: () => [],
  groupTwoTitle: "Humans do this:",
  groupTwoCards: () => [],
  ctaText: "See the multi-agent system in action",
  ctaLink: null,
});

const emit = defineEmits<{ "cta-clicked": [] }>();

const cardBackgroundStyle = computed(() =>
  isLight.value
    ? "border-bottom:1px solid #491C7F;background:linear-gradient(180deg,rgba(210,144,255,0) 0%,rgba(210,144,255,0.2) 100%)"
    : "border-bottom:1px solid #491C7F;background:linear-gradient(180deg,rgba(50,11,78,0) 0%,rgba(70,14,109,0.3) 100%)"
);
</script>

<template>
  <section
    class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]"
    aria-label="The Handoff"
  >
    <!-- Section heading -->
    <h2
      class="text-[28px] lg:text-[48px] font-semibold leading-tight text-center font-avant-demi"
      style="
        background: linear-gradient(92deg, #12dbff -3.57%, #ba55fe 80.72%);
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      "
    >
      {{ heading }}
    </h2>
    <p
      class="text-[16px] lg:text-[18px] font-normal tracking-[0.36px] mt-4 px-4 text-center max-w-[720px] mx-auto font-avant lg:opacity-70"
      :class="isLight ? 'text-black' : 'text-white'"
    >
      {{ subtitle }}
    </p>

    <!-- Group One -->
    <div class="mt-10">
      <h3
        class="text-[20px] lg:text-[24px] font-semibold text-center font-avant-demi mb-6"
        :class="isLight ? 'text-[#000]' : 'text-white'"
      >
        {{ groupOneTitle }}
      </h3>
      <div
        class="flex flex-col items-center lg:flex-row lg:justify-center gap-3"
      >
        <div
          v-for="card in groupOneCards"
          :key="card.title"
          class="flex flex-col items-start justify-start pt-9 pb-4 px-4 min-h-[206px] w-full max-w-[340px] lg:max-w-[305px]"
          :style="cardBackgroundStyle"
        >
          <div class="w-[50px] h-[50px] flex-shrink-0">
            <WrIcon
              v-if="card.svgIcon"
              :name="card.svgIcon"
              class="size-8 text-[#501D71] dark:text-white"
            />
            <WrSVGIcon
              v-else-if="card.icon"
              :name="card.icon"
              class="w-full h-full size-[50px] text-[#501D71] dark:text-white"
            />
          </div>
          <h4
            class="mt-[6px] font-avant text-[20px] font-normal"
            :class="isLight ? 'text-[#000]' : 'text-[#BCB2C3]'"
          >
            {{ card.title }}
          </h4>
          <p
            class="mt-1 text-left font-avant w-full text-[15px] font-normal leading-5 tracking-[0.3px] text-[#AF66E4]"
          >
            {{ card.description }}
          </p>
        </div>
      </div>
    </div>

    <!-- Group Two -->
    <div class="mt-10">
      <h3
        class="text-[20px] lg:text-[24px] font-semibold text-center font-avant-demi mb-6"
        :class="isLight ? 'text-[#000]' : 'text-white'"
      >
        {{ groupTwoTitle }}
      </h3>
      <div
        class="flex flex-col items-center lg:flex-row lg:justify-center gap-3"
      >
        <div
          v-for="card in groupTwoCards"
          :key="card.title"
          class="flex flex-col items-start justify-start pt-9 pb-4 px-4 min-h-[206px] w-full max-w-[340px] lg:max-w-[305px]"
          :style="cardBackgroundStyle"
        >
          <div class="w-[50px] h-[50px] flex-shrink-0">
            <WrIcon
              v-if="card.svgIcon"
              :name="card.svgIcon"
              class="size-8 text-[#501D71] dark:text-white"
            />
            <WrSVGIcon
              v-else-if="card.icon"
              :name="card.icon"
              class="w-full h-full size-[50px] text-[#501D71] dark:text-white"
            />
          </div>
          <h4
            class="mt-[6px] font-avant text-[20px] font-normal"
            :class="isLight ? 'text-[#000]' : 'text-[#BCB2C3]'"
          >
            {{ card.title }}
          </h4>
          <p
            class="mt-1 text-left font-avant w-full text-[15px] font-normal leading-5 tracking-[0.3px] text-[#AF66E4]"
          >
            {{ card.description }}
          </p>
        </div>
      </div>
    </div>

    <!-- CTA Button -->
    <div class="flex justify-center mt-8 lg:mt-12">
      <WrButton
        :link="ctaLink || undefined"
        @click="!ctaLink ? emit('cta-clicked') : undefined"
        container-class="h-[40px] lg:h-[48px]"
        append-class="text-sm lg:font-normal px-3 lg:px-5"
      >
        {{ ctaText }}
      </WrButton>
    </div>
  </section>
</template>
